import { Link, useNavigate } from "@tanstack/react-router";
import { Menu, X, Globe, ChevronDown, Mail } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { MdEmail } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Select, MenuItem, FormControl } from "@mui/material";

import Container from "@/components/ui/Container";
import { useAuth } from "@/features/auth/hooks/useAuth";
import logo from "@/assets/Logo.svg"
import { useQuery } from "@tanstack/react-query";
import { getLanguages } from "@/api/language.api";


export default function Header({ onOpenPlans }: { onOpenPlans?: () => void }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: languages } = useQuery({
    queryKey: ["languages", i18n.resolvedLanguage],
    queryFn: () => getLanguages(),
    staleTime: 0,
  });

  const [selectedLang, setSelectedLang] = useState<any>(null);

  // Sync selectedLang with i18n's current language when languages are loaded
  useEffect(() => {
    if (languages && languages.length > 0) {
      // Get current language from i18n (which persists in localStorage)
      const currentLangCode = i18n.language || i18n.resolvedLanguage;

      // Find the matching language object from the API
      let matchedLang = languages.find(l => l.iso_code === currentLangCode);

      // If no match found (e.g., first time user), use first language
      if (!matchedLang) {
        matchedLang = languages[0];
        i18n.changeLanguage(matchedLang.iso_code);
      }

      // Only update state if it's different to avoid infinite loops
      if (!selectedLang || selectedLang.code !== matchedLang.code) {
        setSelectedLang(matchedLang);
      }
    }
  }, [languages, i18n.language]);

  const handlePlanClick = () => {
    onOpenPlans?.();
    setMobileOpen(false);
  };

  const closeMobile = () => setMobileOpen(false);

  const handleSmoothScroll = async (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();

    // Navigate to home only if not already there
    const currentPath = window.location.pathname;
    if (currentPath !== '/') {
      await navigate({ to: '/' });
    }

    // Use requestAnimationFrame to ensure DOM is ready before scrolling
    requestAnimationFrame(() => {
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        closeMobile();
      }
    });
  };



  const { user, isAuthenticated, logout } = useAuth();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogin = () => {
    window.location.href = "/auth/login";
  }

  const handleRegister = () => {
    window.location.href = "/auth/register";
  }

  const changeChangePassword = () => {
    window.location.href = "/profile/change-password";
  }

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [userMenuRef]);

  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || "U";
  };

  return (
    <div className="flex flex-col w-full">
      {/* Top Bar */}
      <div className="bg-[#187A85] text-white py-2 text-sm">
        <Container className="flex items-center justify-between">
          <span className="font-medium !hidden">
            {t('header.topBar.deals')}
          </span>
          <span></span>
          <a
            href="mailto:support@travelhug.ai"
            className="flex items-center gap-2 hover:text-gray-200 transition-colors"
          >
            <MdEmail className="text-lg" />
            <span>{t('header.topBar.support')}</span>
          </a>
        </Container>
      </div>

      {/* Main Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <Container className="flex items-center justify-between py-3 md:py-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2"
            onClick={closeMobile}
          >
            <img
              src={logo}
              alt="TravelHug Logo"
              className="h-16 w-auto"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8 text-slate-700 font-medium">
            <Link
              className="text-[#e55a28] font-bold"
              to="/"
            >
              {t('header.nav.home')}
            </Link>

            {/* Plan as button */}
            <button
              type="button"
              onClick={onOpenPlans}
              className="hover:text-teal-700 cursor-pointer transition-colors"
              style={{ display: onOpenPlans ? 'inline' : 'none' }}
            >
              {t('header.nav.plan')}
            </button>

            <a
              className="hover:text-teal-700 transition-colors"
              href="/#about"
              onClick={(e) => handleSmoothScroll(e, 'about')}
            >
              {t('header.nav.about')}
            </a>
            <a
              className="hover:text-teal-700 transition-colors"
              href="/#contact"
              onClick={(e) => handleSmoothScroll(e, 'contact')}
            >
              {t('header.nav.contact')}
            </a>

            {/* Language Selector */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={selectedLang?.code || ''}
                onChange={(e) => {
                  const lang = languages?.find(l => l.code === e.target.value);
                  if (lang) {
                    setSelectedLang(lang);
                    i18n.changeLanguage(lang.iso_code);
                  }
                }}
                displayEmpty
                IconComponent={() => null}
                renderValue={(selected) => {
                  if (!selected) return '';
                  const lang = languages?.find(l => l.code === selected);
                  return (
                    <div className="flex items-center gap-2">
                      <span className="text-base">{lang?.flag}</span>
                      <span>{lang?.iso_code.toUpperCase()}</span>
                      <ChevronDown size={16} className="text-slate-400 ml-2" />
                    </div>
                  );
                }}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none'
                  },
                  color: '#334155',
                  fontWeight: 500,
                  '&:hover': {
                    color: '#0f766e'
                  }
                }}
              >
                {languages?.slice(0, 2)?.map((lang) => (
                  <MenuItem key={lang.code} value={lang.code}>
                    <div className="flex items-center gap-2">
                      <span className="text-lg">{lang.flag}</span>
                      <span>{lang.name}</span>
                    </div>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Auth Buttons or User Profile */}
            <div className="flex items-center gap-4 ml-2">
              {isAuthenticated ? (
                <div className="relative" ref={userMenuRef}>
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 hover:bg-gray-100 p-1 pr-3 rounded-full transition-colors"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#187A85] text-white flex items-center justify-center font-bold text-lg overflow-hidden">
                      {user?.profile_photo_url ? (
                        <img src={user.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        getInitials()
                      )}
                    </div>
                    <ChevronDown size={16} />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-48 bg-white border border-gray-100 rounded-lg shadow-lg py-1 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username}
                        </p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                      </div>
                      <Link
                        to="/profile"
                        className="block w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-gray-700 transition-colors"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        {t('header.nav.profile') || "My Profile"}
                      </Link>
                      <button
                        onClick={changeChangePassword}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-blue-600 transition-colors"
                      >
                        {t('auth.changePassword.title')}
                      </button>
                      <button
                        onClick={logout}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm text-red-600 transition-colors"
                      >
                        {t('header.auth.logout')}
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button
                    onClick={handleLogin}
                    type="button"
                    className="hover:text-teal-700 font-medium transition-colors"
                  >
                    {t('header.auth.login')}
                  </button>
                  <button
                    onClick={handleRegister}
                    type="button"
                    className="bg-[#e55a28] hover:bg-[#d64d1f] text-white px-6 py-2 rounded-md font-medium transition-colors shadow-sm"
                  >
                    {t('header.auth.signup')}
                  </button>
                </>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button
            type="button"
            className="md:hidden inline-flex items-center justify-center rounded-full border border-slate-200 p-2 text-slate-700 hover:bg-slate-50"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </Container>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white absolute w-full left-0 top-full shadow-lg z-40">
            <Container className="py-4">
              <nav className="flex flex-col gap-4 text-base text-slate-700">
                <Link
                  className="py-2 hover:text-teal-700 font-medium"
                  to="/"
                  onClick={closeMobile}
                >
                  {t('header.nav.home')}
                </Link>

                <button
                  type="button"
                  onClick={handlePlanClick}
                  className="py-2 text-left hover:text-teal-700 font-medium"
                >
                  {t('header.nav.plan')}
                </button>

                <a
                  className="py-2 hover:text-teal-700 font-medium"
                  href="/#about"
                  onClick={(e) => handleSmoothScroll(e, 'about')}
                >
                  {t('header.nav.about')}
                </a>
                <a
                  className="py-2 hover:text-teal-700 font-medium"
                  href="/#contact"
                  onClick={(e) => handleSmoothScroll(e, 'contact')}
                >
                  {t('header.nav.contact')}
                </a>

                <div className="border-t border-gray-100 my-2 pt-4 flex flex-col gap-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{t('header.nav.language')}</span>
                    <div className="flex gap-2">
                      {languages?.slice(0, 2)?.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => {
                            setSelectedLang(lang);
                            i18n.changeLanguage(lang.iso_code);
                          }}
                          className={`text-lg p-1 rounded ${selectedLang?.iso_code === lang.code ? 'bg-gray-100' : ''}`}
                          title={lang.name}
                        >
                          {lang.flag}
                        </button>
                      ))}
                    </div>
                  </div>

                  {isAuthenticated ? (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-3 py-2">
                        <div className="w-10 h-10 rounded-full bg-[#187A85] text-white flex items-center justify-center font-bold text-lg overflow-hidden">
                          {user?.profile_photo_url ? (
                            <img src={user.profile_photo_url} alt="Profile" className="w-full h-full object-cover" />
                          ) : (
                            getInitials()
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {user?.first_name ? `${user.first_name} ${user.last_name}` : user?.username}
                          </p>
                          <p className="text-xs text-gray-500">{user?.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/profile"
                        className="w-full py-2 border border-gray-200 text-gray-700 rounded-md font-medium hover:bg-gray-50 text-center block"
                        onClick={closeMobile}
                      >
                        {t('header.nav.profile') || "My Profile"}
                      </Link>
                      <button
                        onClick={logout}
                        className="w-full py-2 border border-red-200 text-red-600 rounded-md font-medium hover:bg-red-50"
                      >
                        {t('header.auth.logout')}
                      </button>
                      <button
                        onClick={() => {
                          changeChangePassword();
                          closeMobile();
                        }}
                        className="w-full py-2 border border-blue-200 text-blue-600 rounded-md font-medium hover:bg-blue-50 mt-2"
                      >
                        {t('auth.changePassword.title')}
                      </button>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      <button
                        onClick={handleLogin}
                        className="w-full py-2 border border-slate-300 rounded-md font-medium text-slate-700">
                        {t('header.auth.login')}
                      </button>
                      <button
                        onClick={handleRegister}
                        className="w-full py-2 bg-[#e55a28] text-white rounded-md font-medium">
                        {t('header.auth.signup')}
                      </button>
                    </div>
                  )}
                </div>
              </nav>
            </Container>
          </div>
        )}
      </header>
    </div>
  );
}
